import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';

interface IBannerProps {
  /** The visual appearance of the banner */
  appearance?: 'caution' | 'danger' | 'primary' | 'neutral';
  /** Additional CSS class name */
  className?: string;
  /** The content to be displayed inside the banner */
  children?: ReactNode;
  /** Custom data-cy attribute for testing */
  dataCy?: string;
  /** The function to be called when the banner is clicked */
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
}

const Layout = styled.div<Partial<IBannerProps>>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: ${toRem(theme.button.height.small)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.xl)};
  background-color: ${theme.color.warning[25]};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  text-align: center;
  color: ${theme.color.neutral[900]};
  z-index: ${theme.zIndex.banner};

  @media (min-width: ${theme.breakpoint.small}px) {
    min-height: ${toRem(theme.button.height.large)};
    padding: ${toRem(theme.space.xs)} ${toRem(theme.space.xl)};
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
  }

  ${({ appearance }) =>
    appearance === 'danger' &&
    css`
      background-color: ${theme.color.danger[50]};
      color: ${theme.color.danger[25]};
    `}

  ${({ appearance }) =>
    appearance === 'primary' &&
    css`
      background-color: ${theme.color.primary[500]};
      color: ${theme.color.white};
    `}

  ${({ appearance }) =>
    appearance === 'neutral' &&
    css`
      background-color: ${theme.color.neutral[100]};
      color: ${theme.color.neutral[900]};
    `}
`;

const Banner = ({ appearance = 'caution', className, children, dataCy = 'banner', onClick, ...rest }: IBannerProps) => (
  <Layout
    appearance={appearance}
    className={className}
    data-cy={dataCy}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={(e) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onClick();
      }
    }}
    {...rest}
  >
    {children}
  </Layout>
);

export default Banner;
